import { Logger } from "@abacus/common";

import { Neo4JClient, ProfileType } from "@/neo4j";
import { ResourceNotFoundError } from "@/resourceNotFoundError";
import { ResourceCreationError } from "@/resourceCreationError";
import { JobName, Runner } from "@/types";

import { Email, CreateAbacusProfileTaskConfig } from "./types";
import { isMaskedEmail } from "./utils";

export class CreateAbacusProfileTask implements Runner {
  creatorIdentityId: string;
  envNeo4JClient: Neo4JClient;
  logger?: Logger;
  prodNeo4JClient: Neo4JClient;

  constructor({
    creatorIdentityId,
    envNeo4JClient,
    logger,
    prodNeo4JClient,
  }: CreateAbacusProfileTaskConfig) {
    this.creatorIdentityId = creatorIdentityId;
    this.envNeo4JClient = envNeo4JClient;
    this.logger = logger;
    this.prodNeo4JClient = prodNeo4JClient;
  }

  async run(email: Email): Promise<boolean> {
    // Get identity from Prod
    const prodIdentity = await this.prodNeo4JClient.getIdentityByEmail({
      email,
    });
    if (prodIdentity == null) {
      throw new ResourceNotFoundError("identity", email);
    }

    // Get identity from target env
    const identityId = prodIdentity.id;
    const envIdentity = await this.envNeo4JClient.getIdentityById({
      identityId,
    });
    if (envIdentity == null) {
      throw new ResourceNotFoundError("identity", email);
    }

    // Check if email is masked
    const isMasked = isMaskedEmail(prodIdentity.email, envIdentity.email);

    // Check if the Abacus profile already exists in target env
    let profile = await this.envNeo4JClient.getProfileByTypeAndIdentityId({
      identityId,
      profileType: ProfileType.AbacusProfile,
    });

    // Check if profile already created
    const hasProfile = profile != null;

    this.logger?.info(
      [
        hasProfile ? `Profile already created:` : `Creating profile:`,
        ` Email: ${prodIdentity.email}`,
        `, Identity ID: ${prodIdentity.id}`,
        `, Profile Type: ${profile?.profileType}`,
      ].join(""),
    );

    // Skip if profile already created
    if (hasProfile) {
      return isMasked;
    }

    // Create the Abacus profile in target env
    profile = await this.envNeo4JClient.createAbacusProfile({
      identityId,
      createdBy: this.getCreatedBy(),
    });

    // Check if the Abacus profile was created successfully
    if (profile == null) {
      throw new ResourceCreationError("profile");
    }

    // Log success
    this.logger?.info(
      [
        `User created:`,
        `Email: ${prodIdentity.email},`,
        `Identity ID: ${prodIdentity.id},`,
        `Profile Type: ${profile?.profileType}`,
      ].join(" "),
    );

    return isMasked;
  }

  private getCreatedBy(): string {
    const job = JobName.CreateAbacusProfiles;
    const id = this.creatorIdentityId;
    return `costco/${job}/id: ${id}`;
  }
}
